import type { SocialProfileType } from '@/types/socialProfileType';

// Utils
import useActiveGroup from '@/utils/hooks/user/useActiveGroup';
import useSocialProfiles from '@/utils/hooks/swr/useSocialProfiles';
import useActiveSocialProfile from '@/utils/hooks/user/useActiveSocialProfile';
import { useProfilesStore } from '@/utils/zustand/providers/profiles-provider';

export function useManageProfilesAdded() {
    const { activeGroup } = useActiveGroup();
    const { activeSocialProfile } = useActiveSocialProfile();
    const { socialProfiles } = useSocialProfiles({ revalidateIfStale: false });
    const profilesStore = useProfilesStore((state) => state);

    /**
     * Manage profiles added with an active group
     */
    const manageActiveGroup = (profilesAdded: SocialProfileType[]) => {
        const activeGroupProfiles = activeGroup?.social_profiles?.filter(
            (profile) => profile.active
        );
        profilesStore.manageProfiles(
            [...profilesAdded, ...activeGroupProfiles],
            profilesAdded[0].id
        );
    };

    /**
     * Manage profiles added with active multi selection
     */
    const manageMultiProfiles = (profilesAdded: SocialProfileType[]) => {
        const currentProfiles = socialProfiles.filter((profile) => {
            return profilesStore.activeMultiSelectionProfiles.socialProfileIds.includes(profile.id);
        });
        profilesStore.manageProfiles([...profilesAdded, ...currentProfiles], profilesAdded[0].id);
    };

    const manageProfilesAdded = (profilesAdded: SocialProfileType[]) => {
        if (!profilesAdded?.length) return;

        const activeProfileId = profilesAdded[0].id;
        const hasActiveGroup = Boolean(activeGroup);
        const hasMultiProfiles =
            profilesStore?.activeMultiSelectionProfiles?.socialProfileIds?.length > 0;

        if (hasActiveGroup) {
            manageActiveGroup(profilesAdded);
            return;
        }

        if (hasMultiProfiles) {
            manageMultiProfiles(profilesAdded);
            return;
        }

        /**
         * Manage profiles added with an active profile
         */
        profilesStore.manageProfiles([...profilesAdded, activeSocialProfile], activeProfileId);
    };

    return { manageProfilesAdded };
}
